import { Icons, Images } from "assets";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CustomAccordion from "shared/components/common/customAccordion";
import CustomChart from "shared/components/common/customChart";
import YearPicker from "shared/components/common/yearPicker";
import StatsCard from "shared/components/dashboard/statsCard";
import SkeletonLoader from "shared/loaders/skeleton";
import { routeConstant } from "shared/routes/routeConstant";
import { GetAllCustomersService } from "shared/services/customers";
import {
  GetDashboardGraph,
  GetDashboardStats,
} from "shared/services/dashboard";
import { GetAllSuppliersService } from "shared/services/suppliers";
import { CustomerType } from "shared/utils/enum";
import styles from "./styles.module.scss";

const DashboardHome = () => {
  const [activeDate, setActiveDate] = useState<{
    name: string | number;
    value: string | number;
  }>({
    name: "",
    value: "",
  });
  const [statsLoading, setStatsLoading] = useState<boolean>(false);
  const [customerLoading, setCustomerLoading] = useState<boolean>(false);
  const [supplierLoading, setSupplierLoading] = useState<boolean>(false);
  const [openSelection, setOpenSelection] = useState<boolean>(false);
  const [stats, setStats] = useState<any>({});
  const [customers, setCustomers] = useState<any[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [lineChartData, setLineChartData] = useState<any>({});
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleYearPicker = () => setOpenSelection((prev) => !prev);

  const handleAccordionToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const handleGetDashboardStats = () => {
    setStatsLoading(true);
    GetDashboardStats()
      .then(({ data, status }) => {
        if (status) {
          setStats(data?.data);
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setStatsLoading(false);
      });
  };

  const handleGetCustomers = () => {
    setCustomerLoading(true);
    GetAllCustomersService(1, 2)
      .then(({ data, status }) => {
        if (status) {
          setCustomers(data?.data?.data);
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setCustomerLoading(false);
      });
  };

  const handleGetSuppliers = () => {
    setSupplierLoading(true);
    GetAllSuppliersService(1, 2)
      .then(({ data, status }) => {
        if (status) {
          setSuppliers(data?.data?.data);
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        setSupplierLoading(false);
      });
  };

  const handleGetDashboardGraphData = () => {
    GetDashboardGraph(Number(activeDate?.value))
      .then(({ data, status }) => {
        if (status) {
          setLineChartData(data?.data);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  useEffect(() => {
    handleGetDashboardStats();
    handleGetCustomers();
    handleGetSuppliers();

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    handleGetDashboardGraphData();
    // eslint-disable-next-line
  }, [activeDate?.value]);

  return (
    <div className={classNames("flex flex-col gap-5 w-full h-full")}>
      {/* Stats */}
      <div
        className={classNames(
          styles.stats_section,
          "grid md:grid-cols-3 xs:grid-cols-2 grid-cols-1 gap-5"
        )}
      >
        <StatsCard
          title={"Total Customers"}
          count={stats?.totalCustomers}
          icon={<Icons.CustomerIcon />}
          loading={statsLoading}
          linkPath={routeConstant.dashboardCustomers.path}
        />
        <StatsCard
          title={"Total Suppliers"}
          count={stats?.totalSuppliers}
          icon={<Icons.SuppliersIcon />}
          loading={statsLoading}
          linkPath={routeConstant.dashboardSuppliers.path}
        />
        <StatsCard
          title={"Total Transactions"}
          count={stats?.totalTransactions}
          icon={<Icons.TransactionsIcon />}
          loading={statsLoading}
          linkPath={routeConstant.dashboardTransactions.path}
        />
      </div>

      <div className="grid grid-cols-12 gap-4 mt-4">
        <div
          className={classNames(
            styles.salesChartContainer,
            styles.sectionContainer,
            "sm:col-span-8 col-span-12"
          )}
        >
          <div className={classNames(styles.dateContainer, "relative mb-2")}>
            <button className="flex items-center" onClick={toggleYearPicker}>
              {activeDate.name
                ? `Selected Year: ${activeDate.name}`
                : "Choose a Year"}
              <Icons.ChevDown />
            </button>

            <YearPicker
              activeDate={activeDate}
              openSelection={openSelection}
              setOpenSelection={setOpenSelection}
              setActiveDate={setActiveDate}
            />
          </div>
          <h4 className={classNames(styles.chartTitle)}>
            Transactions per year
          </h4>
          <CustomChart
            data={lineChartData}
            type="line"
            yAxisLabel="Transactions"
            seriesName="Amount"
            curveStyle="smooth"
          />
        </div>

        <div
          className={classNames(
            styles.listContainer,
            styles.sectionContainer,
            "sm:col-span-4 col-span-12"
          )}
        >
          <h4 className={classNames(styles.chartTitle)}>
            New Customers & Suppliers
          </h4>
          <p className={classNames(styles.desc)}>
            List of newly registered customers and suppliers
          </p>

          <div className={classNames(styles.customerWrapper)}>
            {/* Customers */}
            <CustomAccordion
              title="Customers"
              isOpen={openIndex === 0}
              onClick={() => handleAccordionToggle(0)}
            >
              {customerLoading ? (
                <>
                  {Array.from({ length: 2 }).map((_, index) => {
                    return (
                      <div
                        key={index}
                        className={classNames(
                          styles.customerItem,
                          "flex items-center gap-2"
                        )}
                      >
                        <SkeletonLoader
                          className={classNames(styles.imgContainer)}
                        />
                        <div className="flex flex-col gap-1">
                          <SkeletonLoader
                            className={classNames(styles.smallTitleLoader)}
                          />
                          <SkeletonLoader
                            className={classNames(styles.descLoader)}
                          />
                        </div>
                      </div>
                    );
                  })}
                </>
              ) : (
                <>
                  {customers?.map((customer, index) => (
                    <div
                      key={index}
                      className={classNames(styles.customerItem)}
                    >
                      <div className="">
                        <h5
                          className={classNames(
                            styles.smallTitle,
                            "flex-wrap",
                            customer?.user?.fullname.length > 10
                              ? "break-words"
                              : ""
                          )}
                        >
                          {customer?.user?.fullname}
                        </h5>
                        <p className={classNames(styles.desc)}>
                          {customer?.type === CustomerType.Organization
                            ? "Organization"
                            : "Homeowner"}
                        </p>
                      </div>
                    </div>
                  ))}
                </>
              )}

              <Link
                className={classNames(styles.linkStyle)}
                to={routeConstant.dashboardCustomers.path}
              >
                View All Customers <Icons.ArrowRight />
              </Link>
            </CustomAccordion>

            {/* Suppliers */}
            <CustomAccordion
              title="Suppliers"
              isOpen={openIndex === 1}
              onClick={() => handleAccordionToggle(1)}
            >
              {supplierLoading ? (
                <>
                  {Array.from({ length: 2 }).map((_, index) => {
                    return (
                      <div
                        key={index}
                        className={classNames(
                          styles.customerItem,
                          "flex items-center gap-2"
                        )}
                      >
                        <SkeletonLoader
                          className={classNames(styles.imgContainer)}
                        />
                        <div className="flex flex-col gap-1">
                          <SkeletonLoader
                            className={classNames(styles.smallTitleLoader)}
                          />
                          <SkeletonLoader
                            className={classNames(styles.descLoader)}
                          />
                        </div>
                      </div>
                    );
                  })}
                </>
              ) : (
                <>
                  {suppliers?.map((supplier, index) => (
                    <div
                      key={index}
                      className={classNames(styles.customerItem, "flex  gap-2")}
                    >
                      <div className={classNames(styles.imgContainer)}>
                        <img
                          src={
                            supplier?.avatar
                              ? supplier?.avatar?.full_path
                              : Images.DefaultAvatar
                          }
                          alt="supplier-img"
                        />
                      </div>
                      <div className="flex flex-col justify-center">
                        <h5
                          className={classNames(
                            styles.smallTitle,
                            "flex-wrap",
                            supplier?.user?.fullname.length > 10
                              ? "break-words"
                              : ""
                          )}
                        >
                          {supplier?.user?.fullname}
                        </h5>
                        {supplier?.bio ? (
                          <p className={classNames(styles.desc)}>
                            {supplier?.bio}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </>
              )}

              <Link
                className={classNames(styles.linkStyle)}
                to={routeConstant.dashboardSuppliers.path}
              >
                View All Suppliers <Icons.ArrowRight />
              </Link>
            </CustomAccordion>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
